import { Badge, Divider, Group, PasswordInput, Text, TextInput, Title } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useQuery } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'

// Pending (unsaved) Stripe keys in the organization form. The secret key is
// write-only: the middleware never returns it, so an already-configured org
// shows only a status badge until the super enters a replacement pair.
export interface StripeKeysFormRow {
  publishableKey: string
  secretKey: string
}

// The slice of the host form this component reads and writes (same casting
// convention as PlatformAgreements).
export type StripeHostForm = UseFormReturnType<{ stripeKeys: StripeKeysFormRow }>

interface OrganizationStripeSectionProps {
  organizationId?: string
  form: StripeHostForm
}

export function OrganizationStripeSection({
  organizationId,
  form,
}: OrganizationStripeSectionProps) {
  const orpc = useOrpc()

  const { data: status } = useQuery(
    orpc.getOrganizationStripeStatus.queryOptions({
      input: { organizationId: organizationId ?? '' },
      enabled: Boolean(organizationId),
    }),
  )

  const configured = status?.configured === true

  return (
    <>
      <Divider my="sm" />
      <Group justify="space-between" mb="xs">
        <Title order={4}>Stripe (Visit Co-Pay)</Title>
        {configured ? (
          <Badge color="green" variant="light">
            Configured
          </Badge>
        ) : (
          <Badge color="gray" variant="light">
            Not configured
          </Badge>
        )}
      </Group>

      {configured && (
        <Text size="xs" c="dimmed" mb="xs">
          Publishable key: {status?.publishableKey}. Enter a new key pair below to replace it.
        </Text>
      )}

      <TextInput
        label="Publishable key"
        placeholder="pk_live_… or pk_test_…"
        {...form.getInputProps('stripeKeys.publishableKey')}
      />
      <PasswordInput
        label="Secret key"
        placeholder="sk_live_… or sk_test_…"
        autoComplete="new-password"
        {...form.getInputProps('stripeKeys.secretKey')}
      />
      <Text size="xs" c="dimmed">
        Keys are validated against Stripe and stored server-side on save. The secret key is never
        shown again. Saving also registers the co-pay webhook in the clinic&apos;s Stripe account.
      </Text>
    </>
  )
}
